import { useState, useEffect } from 'react';
import {
  sourceColors, durumColors, katilimColors,
  ALL_DURUMLAR, ALL_KATILIM_DURUMLARI,
  type Lead, type Durum, type LansmanKatilimDurum, type Not,
} from '../../../mocks/leads';

interface LeadDrawerProps {
  lead: Lead | null;
  onClose: () => void;
  onUpdate: (id: string, patch: Partial<Lead>) => void;
}

function WaBtnRow({
  telefon,
  adSoyad,
  leadId,
  onInviteMarked,
}: {
  telefon: string;
  adSoyad: string;
  leadId: string;
  onInviteMarked: () => void;
}) {
  const phone = telefon.replace(/\D/g, '');

  function openWhatsapp(msg: string) {
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
  }

  async function markInviteSent() {
    try {
      const id = leadId.replace('LEAD-', '');

      const res = await fetch('/api/davet_gonder.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: `id=${encodeURIComponent(id)}`,
      });

      const data = await res.json();

      if (data.status === 'success') {
        onInviteMarked();
      } else {
        console.error('Davet gönder update hatası:', data);
      }
    } catch (err) {
      console.error('Davet gönder fetch hatası:', err);
    }
  }

  async function handleInviteSend() {
    openWhatsapp(`Merhaba ${adSoyad}, franchise lansmanımıza sizi özel davet ediyoruz. 🚀`);
    await markInviteSent();
  }

  const buttons = [
    {
      label: 'Daveti Gönder',
      icon: 'ri-send-plane-2-line',
      color: '#25d366',
      action: handleInviteSend,
    },
    {
      label: 'Hatırlatma',
      icon: 'ri-notification-3-line',
      color: '#f59e0b',
      action: () => openWhatsapp(`Merhaba ${adSoyad}, lansmanımız yaklaşıyor! Yerinizi ayırtmayı unutmayın.`),
    },
    {
      label: 'Lansman Linki',
      icon: 'ri-links-line',
      color: '#a78bfa',
      action: () => openWhatsapp(`Merhaba ${adSoyad}, lansman etkinlik linkimiz: https://franchise.example.com/lansman`),
    },
    {
      label: 'WhatsApp Aç',
      icon: 'ri-whatsapp-line',
      color: '#25d366',
      action: () => openWhatsapp(`Merhaba ${adSoyad}`),
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-2">
      {buttons.map((b) => (
        <button
          key={b.label}
          onClick={b.action}
          className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap"
          style={{ background: b.color + '18', color: b.color, border: `1px solid ${b.color}30` }}
        >
          <i className={`${b.icon} text-sm`} />
          {b.label}
        </button>
      ))}
    </div>
  );
}

export default function LeadDrawer({ lead, onClose, onUpdate }: LeadDrawerProps) {
  const [notMetin, setNotMetin] = useState('');
  const [activeTab, setActiveTab] = useState<'bilgi' | 'notlar' | 'aktivite'>('bilgi');

  useEffect(() => {
    if (lead) setActiveTab('bilgi');
  }, [lead?.id]);

  if (!lead) return null;

  const srcColor = sourceColors[lead.kaynak] || '#6b7280';
  const durColor = durumColors[lead.durum] || '#94a3b8';
  const katColor = katilimColors[lead.lansman_katilim_durumu] || '#94a3b8';

  function handleStatusChange(durum: Durum) {
    onUpdate(lead.id, { durum, guncelleme_tarihi: new Date().toISOString() });
  }

  function handleKatilimChange(lansman_katilim_durumu: LansmanKatilimDurum) {
    const davetGonderildi = ['Davet Gönderildi', 'Katılacak', 'Katıldı'].includes(lansman_katilim_durumu);

    onUpdate(lead.id, {
      lansman_katilim_durumu,
      davetGonderildi,
      guncelleme_tarihi: new Date().toISOString(),
    });
  }

  function handleAddNote() {
    if (!notMetin.trim()) return;

    const newNote: Not = {
      id: `n-${Date.now()}`,
      metin: notMetin.trim(),
      tarih: new Date().toISOString(),
      yazar: 'Yönetici',
    };

    onUpdate(lead.id, {
      notlar: [...lead.notlar, newNote],
      guncelleme_tarihi: new Date().toISOString(),
    });

    setNotMetin('');
  }

  const tabs = [
    { key: 'bilgi', label: 'Bilgi', icon: 'ri-user-line' },
    { key: 'notlar', label: `Notlar (${lead.notlar.length})`, icon: 'ri-sticky-note-line' },
    { key: 'aktivite', label: 'Aktivite', icon: 'ri-timeline-line' },
  ] as const;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={onClose} />

      <aside className="fixed top-0 right-0 h-full w-full max-w-[520px] bg-[#0b0e1a] border-l border-white/[0.08] z-50 flex flex-col overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/[0.06] bg-[#0d1120]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <span className="text-amber-400 font-bold text-base">{lead.ad_soyad.charAt(0)}</span>
            </div>
            <div>
              <p className="text-white font-bold text-base leading-tight">{lead.ad_soyad}</p>
              <p className="text-white/30 text-xs font-mono">{lead.id} · {lead.telefon}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.05] hover:bg-white/[0.10] text-white/30 hover:text-white transition-all cursor-pointer"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>

        <div className="flex items-center gap-2 px-6 py-3 border-b border-white/[0.04] flex-wrap">
          <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: srcColor + '22', color: srcColor }}>
            {lead.kaynak}
          </span>
          <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: durColor + '22', color: durColor }}>
            {lead.durum}
          </span>
          <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: katColor + '22', color: katColor }}>
            {lead.lansman_katilim_durumu}
          </span>
          {lead.manuel_mi && (
            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-400">
              Manuel
            </span>
          )}
        </div>

        <div className="flex border-b border-white/[0.06]">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-semibold transition-all cursor-pointer ${
                activeTab === t.key ? 'text-amber-400 border-b-2 border-amber-400' : 'text-white/30 hover:text-white/60'
              }`}
            >
              <i className={t.icon} />
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {activeTab === 'bilgi' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Ad Soyad', val: lead.ad_soyad },
                  { label: 'Telefon', val: lead.telefon, mono: true },
                  { label: 'Kaynak', val: lead.kaynak },
                  { label: 'İş Seviyesi', val: lead.is_seviyesi },
                  { label: 'IP Adresi', val: lead.ip_adresi, mono: true },
                  { label: 'User Agent', val: lead.user_agent, mono: true },
                  { label: 'Oluşturma', val: new Date(lead.olusturma_tarihi).toLocaleString('tr-TR') },
                  { label: 'Güncelleme', val: new Date(lead.guncelleme_tarihi).toLocaleString('tr-TR') },
                  ...(lead.manychat_user_id ? [{ label: 'ManyChat ID', val: lead.manychat_user_id, mono: true }] : []),
                  ...(lead.davet_gonderim_tarihi
                    ? [{ label: 'Davet Tarihi', val: new Date(lead.davet_gonderim_tarihi).toLocaleString('tr-TR') }]
                    : []),
                ].map(({ label, val, mono }) => (
                  <div key={label}>
                    <p className="text-white/25 text-[10px] font-semibold uppercase tracking-widest mb-0.5">{label}</p>
                    <p className={`text-white/70 text-xs ${mono ? 'font-mono' : 'font-medium'}`}>{val}</p>
                  </div>
                ))}
              </div>

              <div className="space-y-3 pt-1">
                <div>
                  <p className="text-white/25 text-[10px] font-semibold uppercase tracking-widest mb-2">Durum Güncelle</p>
                  <div className="flex flex-wrap gap-1.5">
                    {ALL_DURUMLAR.map((d) => (
                      <button
                        key={d}
                        onClick={() => handleStatusChange(d)}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all cursor-pointer ${
                          lead.durum === d ? 'ring-1' : 'opacity-50 hover:opacity-80'
                        }`}
                        style={{ background: durumColors[d] + '25', color: durumColors[d], ringColor: durumColors[d] }}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-white/25 text-[10px] font-semibold uppercase tracking-widest mb-2">Katılım Durumu</p>
                  <div className="flex flex-wrap gap-1.5">
                    {ALL_KATILIM_DURUMLARI.map((k) => (
                      <button
                        key={k}
                        onClick={() => handleKatilimChange(k)}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all cursor-pointer ${
                          lead.lansman_katilim_durumu === k ? 'ring-1' : 'opacity-50 hover:opacity-80'
                        }`}
                        style={{ background: katilimColors[k] + '25', color: katilimColors[k] }}
                      >
                        {k}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <p className="text-white/25 text-[10px] font-semibold uppercase tracking-widest mb-2">WhatsApp İşlemleri</p>
                <WaBtnRow
                  telefon={lead.telefon}
                  adSoyad={lead.ad_soyad}
                  leadId={lead.id}
                  onInviteMarked={() =>
                    onUpdate(lead.id, {
                      davetGonderildi: true,
                      lansman_katilim_durumu: 'Davet Gönderildi',
                      davet_gonderim_tarihi: new Date().toISOString(),
                      guncelleme_tarihi: new Date().toISOString(),
                    })
                  }
                />
              </div>
            </div>
          )}

          {activeTab === 'notlar' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <textarea
                  value={notMetin}
                  onChange={(e) => setNotMetin(e.target.value.slice(0, 500))}
                  placeholder="Yeni not ekleyin..."
                  rows={3}
                  className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-4 py-3 text-white text-sm placeholder:text-white/20 focus:outline-none focus:border-amber-500/50 resize-none"
                />
                <div className="flex items-center justify-between">
                  <span className="text-white/20 text-xs">{notMetin.length}/500</span>
                  <button
                    onClick={handleAddNote}
                    disabled={!notMetin.trim()}
                    className="px-4 py-2 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-xs font-semibold transition-all cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    <i className="ri-add-line mr-1" />
                    Not Ekle
                  </button>
                </div>
              </div>

              {lead.notlar.length === 0 ? (
                <div className="py-8 flex flex-col items-center gap-2 text-white/20">
                  <i className="ri-sticky-note-line text-3xl" />
                  <p className="text-sm">Henüz not yok</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {[...lead.notlar].reverse().map((n) => (
                    <div key={n.id} className="rounded-lg bg-white/[0.04] border border-white/[0.07] p-4">
                      <p className="text-white/70 text-sm leading-relaxed">{n.metin}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <i className="ri-user-line text-white/20 text-xs" />
                        <span className="text-white/25 text-xs">{n.yazar}</span>
                        <span className="text-white/15 text-xs">·</span>
                        <span className="text-white/25 text-xs">{new Date(n.tarih).toLocaleString('tr-TR')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'aktivite' && (
            <div className="space-y-1">
              {[
                { icon: 'ri-file-add-line', color: '#94a3b8', text: 'Başvuru oluşturuldu', tarih: lead.olusturma_tarihi },
                ...(lead.davet_gonderim_tarihi
                  ? [{ icon: 'ri-whatsapp-line', color: '#25d366', text: 'WhatsApp daveti gönderildi', tarih: lead.davet_gonderim_tarihi }]
                  : []),
                ...lead.notlar.map((n) => ({
                  icon: 'ri-sticky-note-line',
                  color: '#f59e0b',
                  text: `Not eklendi: "${n.metin.slice(0, 40)}..."`,
                  tarih: n.tarih,
                })),
                { icon: 'ri-refresh-line', color: '#a78bfa', text: `Durum: ${lead.durum}`, tarih: lead.guncelleme_tarihi },
              ]
                .sort((a, b) => b.tarih.localeCompare(a.tarih))
                .map((ev, i) => (
                  <div key={i} className="flex gap-3 pb-4 relative">
                    <div className="flex flex-col items-center">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 z-10" style={{ background: ev.color + '22' }}>
                        <i className={`${ev.icon} text-xs`} style={{ color: ev.color }} />
                      </div>
                      <div className="w-px flex-1 mt-1 bg-white/[0.06]" />
                    </div>
                    <div className="pt-1">
                      <p className="text-white/60 text-xs">{ev.text}</p>
                      <p className="text-white/20 text-[10px] mt-0.5">{new Date(ev.tarih).toLocaleString('tr-TR')}</p>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </aside>
    </>
  );
}